import type { Guess, QuestionResult } from './types'

export const MAG_LABELS = ['1','10','100','1K','10K','100K','1M','10M','100M','1B','10B','100B','1T','10T','100T','1Qa','10Qa','100Qa','1Qi']
export const MAX_EXP = MAG_LABELS.length - 1

/** Plain-words anchor per magnitude. Deliberately avoids facts that appear in the question bank. */
export const MAG_ANCHORS: string[] = [
  'just you',
  'a five-a-side match, both teams',
  'a packed double-decker bus',
  'a village',
  'a lower-league football crowd',
  'Wembley, full, plus a bit',
  'everyone in Birmingham',
  'all of Greater London',
  'about the population of Japan',
  '1 in 8 people on Earth',
  'more than everyone alive today',
  'seconds in 3,000 years',
  'seconds in 32,000 years',
  'seconds in 320,000 years',
  'seconds in 3 million years',
  'seconds in 32 million years',
  'seconds in 320 million years',
  'seconds in 3 billion years',
  'seconds in 32 billion years — older than the universe',
]

export const TIERS = [
  { points: 5, emoji: '🎯', name: 'Bang on', max: 1.1 },
  { points: 4, emoji: '👍', name: 'Nailed it', max: 1.5 },
  { points: 3, emoji: '🙂', name: 'Close', max: 3 },
  { points: 2, emoji: '😐', name: 'A bit out', max: 10 },
  { points: 1, emoji: '😬', name: 'Miles out', max: 100 },
  { points: 0, emoji: '💀', name: 'Way off', max: Infinity },
] as const
export const TOP_POINTS = 5
/** "Sharp" = within 1.5× of the truth (🎯 or 👍) — the old top-tier semantics. */
export const SHARP_MIN = 4

/** Bands: 6 bands of three stops (×1/×10/×100) + the single terminal 1Qi stop. */
export const BANDS = [
  { label: '1s', name: 'units', start: 0 },
  { label: 'K', name: 'thousands', start: 3 },
  { label: 'M', name: 'millions', start: 6 },
  { label: 'B', name: 'billions', start: 9 },
  { label: 'T', name: 'trillions', start: 12 },
  { label: 'Qa', name: 'quadrillions', start: 15 },
  { label: 'Qi', name: 'quintillions', start: 18 },
] as const

export function bandOf(stop: number): number { return Math.min(6, Math.floor(stop / 3)) }

/** Continuous score: each order of magnitude off halves it. 1000 = exact. */
export function scoreFor(guess: number, answer: number): number {
  const e = Math.abs(Math.log10(guess / answer))
  return Math.floor(1000 * Math.pow(0.5, e))
}
export const MAX_Q_SCORE = 1000
export const MAX_DAY_SCORE = 5000

export function guessValue(g: Guess): number { return g.digit * 10 ** g.exp }

export function ratioOf(guess: number, answer: number): number {
  return Math.max(guess, answer) / Math.min(guess, answer)
}

export function pointsFor(ratio: number): 0 | 1 | 2 | 3 | 4 | 5 {
  if (ratio <= 1.1) return 5
  if (ratio <= 1.5) return 4
  if (ratio <= 3) return 3
  if (ratio <= 10) return 2
  if (ratio <= 100) return 1
  return 0
}

export function tierFor(points: number) { return TIERS[TOP_POINTS - points] }
export function emojiFor(points: number) { return tierFor(points).emoji }
export function emojiForResult(r: { points: number; timedOut?: boolean }) { return r.timedOut ? '⏰' : emojiFor(r.points) }

export function scoreQuestion(id: string, guess: number, answer: number): QuestionResult {
  const ratio = ratioOf(guess, answer)
  return { id, guess, answer, ratio, points: pointsFor(ratio), qscore: scoreFor(guess, answer) }
}

/** Histogram bin = order of magnitude of the guess, clamped to the input range. */
export function binOf(value: number): number {
  return Math.min(MAX_EXP, Math.max(0, Math.floor(Math.log10(value))))
}
/** Geometric centre of a bin, used as the "crowd guess" for that bin. */
export function binCentre(bin: number): number { return 10 ** (bin + 0.5) }

export function ordersOff(ratio: number): number { return Math.log10(ratio) }

const BIG = [
  [1e18, 'quintillion'], [1e15, 'quadrillion'], [1e12, 'trillion'], [1e9, 'billion'], [1e6, 'million'],
] as const

/** "7.5 quintillion", "11,718", "2" */
export function formatBig(n: number, sig = 3): string {
  if (!Number.isFinite(n)) return '—'
  for (const [v, name] of BIG) {
    if (n >= v) {
      const x = n / v
      const s = x >= 100 ? Math.round(x).toString() : Number(x.toPrecision(sig)).toString()
      return `${s} ${name}`
    }
  }
  if (n >= 1000) return Math.round(n).toLocaleString('en-US')
  return Number(n.toPrecision(sig)).toString()
}

export function formatExact(n: number): string {
  if (n >= 1e21) return n.toExponential(2)
  return Math.round(n).toLocaleString('en-US')
}

/** e.g. "2.3x too high", "40,000x too low", "spot on" */
export function describeMiss(guess: number, answer: number): string {
  const r = ratioOf(guess, answer)
  if (r < 1.05) return 'spot on'
  const rs = r >= 100 ? Math.round(r).toLocaleString('en-US') : r >= 10 ? r.toFixed(0) : r.toFixed(1)
  return `${rs}x too ${guess > answer ? 'high' : 'low'}`
}

export function shareLine(emoji: string, score: number, percentile?: number): string {
  const p = percentile != null ? ` · closer than ${percentile}% today` : ''
  return `${emoji} · ${score.toLocaleString('en-US')}/10,000${p}`
}

/** "within 1.08×" / "48× off" — the reveal framing. */
export function timesOff(ratio: number): string {
  const r = ratio >= 100 ? Math.round(ratio).toLocaleString('en-US') : ratio >= 10 ? ratio.toFixed(0) : ratio.toFixed(2).replace(/0$/, '')
  return `${r}×`
}

/**
 * The reveal badge, gated by how close the guess actually was:
 * exact → "Perfect" · ≤1.1× "just over/under" · ≤1.5× "a touch high/low" ·
 * ≤10× "within N× — too high/low" · ≤100× "out by N×" · beyond "wildly high/low".
 */
export function missBadge(guess: number, answer: number): string {
  if (guess === answer) return 'Perfect'
  const ratio = ratioOf(guess, answer)
  const t = timesOff(ratio)
  const over = guess > answer
  if (ratio <= 1.1) return `within ${t} — just ${over ? 'over' : 'under'}`
  if (ratio <= 1.5) return `within ${t} — a touch ${over ? 'high' : 'low'}`
  if (ratio <= 10) return `within ${t} — too ${over ? 'high' : 'low'}`
  if (ratio <= 100) return `out by ${t} — too ${over ? 'high' : 'low'}`
  return `out by ${t} — wildly ${over ? 'high' : 'low'}`
}
